import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft, MapPin, Phone,
    FileText
} from 'lucide-react';
import { useJobDetails } from '../../../Hooks/useJobDetails';

export default function JobDetails() {
    const navigate = useNavigate();
    const { job, isLoading, error } = useJobDetails();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-500">Loading job details...</div>
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-600">Failed to load job. Please try again.</div>
            </div>
        );
    }

    return (
        <section className="min-h-screen">
            <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
                <div className="px-4 py-4">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">{job.boatMake} {job.boatType} Survey</h1>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">{job.startDate}</span>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs font-medium text-emerald-600">{job.jobNumber}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 py-5 space-y-5">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            <h3 className="text-sm font-bold text-gray-900">Boat Location</h3>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{job.boatLocation}</p>
                        <p className="text-sm text-gray-600">ZIP: {job.zipCode}</p>
                        {job.isBoatInWater && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 uppercase tracking-wide mb-2">Boat is in Water</span>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                        <span className="text-gray-500">Dock Number</span>
                                        <p className="font-semibold">{job.dock || '—'}</p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Slip Number</span>
                                        <p className="font-semibold">{job.slipNumber || '—'}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Phone className="w-4 h-4 text-emerald-600" />
                            <h3 className="text-sm font-bold text-gray-900">Client Contact</h3>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{job.clientName}</p>
                        <p className="text-sm text-gray-600">{job.clientPhone}</p>
                        <p className="text-sm text-gray-600">{job.clientEmail}</p>
                        {job.raw?.quote?.insurance_company_email && (
                            <p className="text-xs text-gray-500 mt-1 italic">Insurance: {job.raw.quote.insurance_company_email}</p>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-4">Boat Details</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="text-xs text-gray-500">Type</span>
                            <p className="font-medium">{job.boatType}</p>
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Year</span>
                            <p className="font-medium">{job.boatYear}</p>
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Manufacturer</span>
                            <p className="font-medium">{job.boatMake}</p>
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Length</span>
                            <p className="font-medium">{job.boatLength}</p>
                        </div>
                    </div>

                    {Object.keys(job.customFields).length > 0 && (
                        <div className="mt-6 pt-5 border-t border-gray-100">
                            <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-3">Additional Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {Object.entries(job.customFields).map(([key, value]) => (
                                    <div key={key} className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                                        <span className="text-[10px] text-gray-500 uppercase font-bold tracking-tight block mb-0.5">{key}</span>
                                        <p className="text-sm font-medium text-gray-900">{value}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-4">Schedule</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <span className="text-xs text-gray-500">Date</span>
                            <p className="font-medium">{job.startDate}</p>
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Time</span>
                            <p className="font-medium">{job.startTime} – {job.endTime}</p>
                        </div>
                        <div>
                            <span className="text-xs text-gray-500">Repeats</span>
                            <p className="font-medium">{job.repeats}</p>
                        </div>
                    </div>
                    {job.visitInstructions && (
                        <div className="mt-4 p-3 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium mb-1">Visit Instructions</p>
                            <p className="text-sm text-gray-800">{job.visitInstructions}</p>
                        </div>
                    )}
                </div>

                {job.surveyors.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <h3 className="text-base font-bold text-gray-900 mb-4">Assigned Surveyors</h3>
                        <div className="space-y-3">
                            {job.surveyors.map((s, i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                                            {s.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">{s.name}</p>
                                            <p className="text-xs text-gray-600">{s.phone}</p>
                                        </div>
                                    </div>
                                    {s.amount > 0 && (
                                        <span className="text-sm font-bold text-emerald-600">${s.amount.toFixed(2)}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}



                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-gray-600" />
                        <h3 className="text-base font-bold text-gray-900">Internal Notes</h3>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Only visible to your team</p>
                    <textarea
                        rows="4"
                        defaultValue={job.internalNotes}
                        placeholder="Add internal notes..."
                        className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                    />
                </div>
            </div>
        </section>
    );
}